import { ReactNode, RefObject, createContext, useContext } from 'react';

import { CheckboxProps } from '@mui/material';
import {
  GridApi,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFilterModel,
  GridGroupNode,
  GridGroupingColDefOverride,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRowClassNameParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  MuiEvent,
} from '@mui/x-data-grid-premium';

import {
  DisabledCheckboxTooltip,
  GeneratikTableColumn,
  ListViewRowSelection,
} from './GeneratikTable.types';
import {
  FilterFieldConfig,
  FilterValues,
  PredefinedFilter,
} from './GeneratikTableFilterPanel.types';
import { FilterChip } from './GeneratikTableHeader.types';
import {
  GroupOption,
  SortOption,
  ToolbarActionButton,
  ToolbarPrimaryButton,
} from './GeneratikTableToolbar.types';
import { UseSortModelActionsReturn } from './hooks/useSortModelActions';
import { TimelineAxis, TimelineGranularity, TimelineVariant } from './timeline/timeline.types';

export interface GeneratikTableContextValue {
  tableId?: string;
  checkboxSelection?: boolean;
  radioSelection?: boolean;
  columns: GeneratikTableColumn[];
  rows: GridValidRowModel[];
  rowCount?: number;
  loading: boolean;
  disableRowSelectionOnClick: boolean;
  isRowSelectable?: (params: GridRowParams) => boolean;
  disabledCheckboxTooltip?: DisabledCheckboxTooltip;
  onDisabledCheckboxClick?: () => void;
  getRowId?: (row: GridValidRowModel) => string | number;
  paginationMode: 'client' | 'server';
  paginationModel: GridPaginationModel;
  pageSizeOptions: number[];
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  sortingMode: 'client' | 'server';
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  disableSortButton?: boolean;
  filterMode: 'client' | 'server';
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  columnVisibilityModel?: Record<string, boolean>;
  campaignColumnVisibility?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  onRowSelectionModelChange?: (selectionModel: GridRowSelectionModel) => void;
  onSelectedRowsChange?: (selectedRows: GridValidRowModel[]) => void;
  onRowClick?: (row: GridValidRowModel) => void;
  onRowDoubleClick?: (row: GridValidRowModel) => void;
  hideToolbar: boolean;
  hidePagination: boolean;
  hideColumnMenu: boolean;
  CustomToolbar?: React.ComponentType;
  columnReordering: boolean;
  pinnedColumns?: GridPinnedColumnFields;
  onPinnedColumnsChange: (pinnedColumns: GridPinnedColumnFields) => void;
  density: 'compact' | 'standard' | 'comfortable';
  height: number | string | 'fill' | 'auto';
  sx?: object;
  title?: string;
  titleIcon?: ReactNode;
  hasBackButton?: boolean;

  hideHeader: boolean;
  hideCount: boolean;
  leftButtons?: ToolbarActionButton[];
  rightButtons?: ToolbarPrimaryButton[] | ReactNode;
  searchValue: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch: boolean;
  showColumns: boolean;
  disableColumnsButton: boolean;
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  sortOptions?: SortOption[];
  sortActions: UseSortModelActionsReturn;
  onColumnHeaderClick: GridEventListener<'columnHeaderClick'>;
  groupOptions?: GroupOption[];
  groupByField?: string | null;
  onGroupChange?: (field: string | null) => void;
  disableGroupButton?: boolean;
  defaultGroupingExpansionDepth?: number;
  viewMode: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  hideCustomToolbar: boolean;
  filterFields: FilterFieldConfig[];
  filterValues: FilterValues;
  onFilterValuesChange?: (values: FilterValues) => void;
  enableContextMenu: boolean;
  columnFilterMap?: Record<string, string>;
  columnRawFieldMap?: Record<string, string | ((row: GridValidRowModel) => unknown)>;
  zoomLevel: number;
  onZoomChange: (level: number) => void;
  activeFilters: FilterChip[];
  onFilterRemove?: (filterId: string) => void;
  onFilterApply?: (values?: FilterValues) => void;
  onFilterReset?: () => void;
  quickFilters?: PredefinedFilter[];
  treeData?: boolean;
  treeDataEnabled: boolean;
  getTreeDataPath?: (row: GridValidRowModel) => string[];
  effectiveGetTreeDataPath?: (row: GridValidRowModel) => Array<string | number>;
  isChildRow?: (row: GridValidRowModel) => boolean;
  isGroupExpandedByDefault?: (node: GridGroupNode) => boolean;
  autosizeOnMount: boolean;
  autosizeOptions?: object;
  calculatePageTotal?: (rows: GridValidRowModel[]) => number | string;
  calculateSelectedTotal?: (rows: GridValidRowModel[]) => number | string;
  currency?: string;
  emptyStateMessage?: string;
  isError?: boolean;
  onRetry?: () => void;
  predefinedFilters?: ReactNode;
  requiresCampaignPeriod: boolean;
  headersRequest?: { pageType: string; resourceId?: string };
  isHeadersLoading?: boolean;
  onTableConfigChange?: (config: { visibleColumns: string[]; columnOrder: string[] }) => void;
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void;
  getRowCanExpand?: (params: GridRowParams) => boolean;
  renderDetailPanel?: (params: GridRowParams) => ReactNode;
  detailPanelHeight: number | 'auto' | ((params: GridRowParams) => number | 'auto');
  detailPanelExpandedRowIds?: GridRowId[];
  onDetailPanelExpandedRowIdsChange?: (ids: GridRowId[]) => void;
  rowHeight?: number;
  renderListViewRow?: (
    row: GridValidRowModel,
    openFilters: () => void,
    selection: ListViewRowSelection,
  ) => ReactNode;
  getGroupItemPrice?: (row: GridValidRowModel) => number | undefined;
  groupPriceForPeriod?: boolean;
  groupRowCountLabel?: (count: number) => string;
  modalActions?: ReactNode;
  stackedModalFooter?: boolean;
  dialogTestId?: string;
  onModalClose?: () => void;
  drawerOpen?: boolean;

  processRowUpdate?: (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel,
  ) => GridValidRowModel | Promise<GridValidRowModel>;
  singleClickEdit?: boolean;
  noTopPadding?: boolean;
  noPadding?: boolean;
  seamless?: boolean;
  plainToolbar?: boolean;
  toolbarStartContent?: ReactNode;
  hideVerticalScrollbar?: boolean;

  // Computed values
  fillHeight: boolean;
  showCustomToolbar: boolean;
  totalCount: number;
  processedColumns: GeneratikTableColumn[];
  defaultGetRowId: (row: GridValidRowModel) => string | number;
  selectionModel: GridRowSelectionModel;
  dataGridKey: string | undefined;
  effectiveColumnVisibilityModel: Record<string, boolean>;
  groupingColDef: GridGroupingColDefOverride;
  tableHeight: number | string;
  pageTotal: number | string | undefined;
  pageSelected: number | string | undefined;
  spacesTotal: number | undefined;
  productionTotal: number | undefined;
  grandTotal: number | undefined;
  selectedRows: GridValidRowModel[];

  // Handlers
  handleFilterClick: (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => void;
  handleRowSelectionChange: (newSelectionModel: GridRowSelectionModel) => void;
  handleRowClick: (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>) => void;
  handleRowDoubleClick: (params: GridRowParams) => void;
  getCellClassName: (params: { field: string; row: GridValidRowModel }) => string;
  getRowClassName: (params: GridRowClassNameParams) => string;
  handleColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
  handleColumnWidthChange: (params: GridColumnResizeParams) => void;
  handlePageChange: (newPage: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  toggleRowSelection: (rowId: GridRowId) => void;
  handleFilterClose: () => void;
  handleFilterApplyInternal: (values?: FilterValues) => void;

  // Refs
  apiRef: React.MutableRefObject<GridApi>;
  gridRootRef: (element: HTMLDivElement | null) => void;
  filterButtonRef: RefObject<HTMLButtonElement | null>;
  filterAnchorEl: HTMLElement | null;
  focusFieldId: string | null;

  // Slots
  ToolbarComponent: React.ComponentType | undefined;
  NoRowsOverlay: React.ComponentType;
  CheckboxSlot: React.ComponentType<CheckboxProps>;
  RadioSlot: React.ComponentType<CheckboxProps>;

  // Timeline variant
  variant: TimelineVariant;
  timelineGranularity: TimelineGranularity;
  hasTimelineColumns: boolean;
  isTimelineActive: boolean;
  /**
   * The axis actually drawn — see useTimelineColumns's own `axis` return field. The toolbar keys
   * the granularity toggle's visibility off this, not `timelineGranularity`'s presence: a
   * bookings rail renders every granularity identically (v5 anchors-only, subdivision pinned to
   * week), so the toggle has nothing left to do there, while a calendar rail (including the
   * bookings-axis's own no-bars fallback) still genuinely differs by granularity.
   */
  effectiveTimelineAxis: TimelineAxis;
  onVariantChange: (variant: TimelineVariant) => void;
  onTimelineGranularityChange: (granularity: TimelineGranularity) => void;

  // i18n
  t: (key: string, options?: Record<string, unknown>) => string;
}

const GeneratikTableContext = createContext<GeneratikTableContextValue | null>(null);

export const GeneratikTableProvider = GeneratikTableContext.Provider;

export function useGeneratikTableContext(): GeneratikTableContextValue {
  const ctx = useContext(GeneratikTableContext);
  if (!ctx) {
    throw new Error('useGeneratikTableContext must be used within a GeneratikTableProvider');
  }
  return ctx;
}

export function useOptionalGeneratikTableContext(): GeneratikTableContextValue | null {
  return useContext(GeneratikTableContext);
}
